import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as storageService from '../services/storageService';
import { Hospital } from '../types';
import { LayoutDashboard, Monitor, LogOut, Building, ArrowRight, HeartPulse, BarChart3 } from 'lucide-react';
import { AdminNavbarUser } from '../components/AdminNavbarUser';

const HospitalRoleSelection: React.FC = () => {
    const navigate = useNavigate();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            const authData = storageService.getAuthData();
            if (!authData) {
                navigate('/login');
                return;
            }

            // Immediately redirect SUPER_ADMINs who land here by mistake (unless impersonating)
            const isImpersonating = window.location.search.includes('impersonate=true');
            if (!isImpersonating && authData.hospitalId === 'ADMIN') {
                navigate('/admin/dashboard');
                return;
            }

            const found = await storageService.getHospitalById(authData.hospitalId);
            setHospital(found || null);
            setLoading(false);
        };
        load();
    }, [navigate]);

    const handleLogout = async () => {
        await storageService.logout();
        navigate('/logout');
    };

    if (loading) return null;

    if (!hospital) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Hospital no configurado</h1>
                <button onClick={handleLogout} className="text-indigo-600 font-bold hover:underline">Cerrar Sesión</button>
            </div>
        );
    }

    const primaryColor = hospital.branding?.primaryColor || '#5B65DC';
    const secondaryColor = hospital.branding?.secondaryColor || '#122056';

    return (
        <div
            className="min-h-screen bg-slate-50 flex flex-col font-sans selection:bg-indigo-100"
            style={{
                '--brand-color-light': `${primaryColor}40`,
                '--brand-color-solid': `${primaryColor}70`
            } as any}
        >
            <style>{`
                .role-card {
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .role-card:hover {
                    box-shadow: 0 25px 50px -12px var(--brand-color-light);
                    transform: translateY(-6px) scale(1.01);
                }
                .role-card:active {
                    transform: translateY(0) scale(0.98);
                    box-shadow: 0 10px 20px -10px var(--brand-color-light);
                    transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .role-card-solid {
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .role-card-solid:hover {
                    box-shadow: 0 30px 60px -15px var(--brand-color-solid);
                    transform: translateY(-6px) scale(1.01);
                    filter: brightness(1.05);
                }
                .role-card-solid:active {
                    transform: translateY(0) scale(0.98);
                    box-shadow: 0 15px 30px -10px var(--brand-color-solid);
                    transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
                }
            `}</style>

            {/* Header / Navbar */}
            <nav className="p-8 flex justify-between items-center max-w-7xl mx-auto w-full">
                <div className="flex items-center gap-3">
                    {hospital.branding?.logoDarkUrl && window.matchMedia('(prefers-color-scheme: dark)').matches ? (
                        <div className="bg-white py-2 px-4 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center h-14 min-w-[3.5rem]" style={{ boxShadow: `0 4px 14px 0 ${primaryColor}30` }}>
                            <img src={hospital.branding.logoDarkUrl} alt="Logo" className="h-full w-auto object-contain" />
                        </div>
                    ) : hospital.branding?.logoUrl ? (
                        <div className="bg-white py-2 px-4 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center h-14 min-w-[3.5rem]" style={{ boxShadow: `0 4px 14px 0 ${primaryColor}30` }}>
                            <img src={hospital.branding.logoUrl} alt="Logo" className="h-full w-auto object-contain" />
                        </div>
                    ) : (
                        <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center h-14 min-w-[3.5rem]" style={{ boxShadow: `0 4px 14px 0 ${primaryColor}30` }}>
                            <HeartPulse className="w-7 h-7" style={{ color: primaryColor }} />
                        </div>
                    )}
                    <div>
                        <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Hospital Status Staff</span>
                        <span className="block text-lg font-black text-slate-800 tracking-tight leading-none">{hospital.name}</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 md:gap-6">
                    <AdminNavbarUser primaryColor={primaryColor} />
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-xs md:text-sm font-bold text-slate-400 hover:text-rose-500 transition-all px-4 py-2 md:px-5 md:py-2.5 rounded-full hover:bg-rose-50 border border-slate-200/50 hover:border-rose-100 shadow-sm hover:shadow active:scale-95"
                    >
                        <span className="hidden sm:inline">Salir de la Estación</span>
                        <span className="sm:hidden">Salir</span>
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </nav>

            <main className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:py-16 mt-4 md:mt-8">
                <div className="text-center mb-10 md:mb-14 animate-fade-in">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-800 mb-3 tracking-tight font-heading">
                        ¿Qué deseas hacer hoy?
                    </h1>
                    <p className="text-slate-500 font-medium text-sm md:text-base max-w-md mx-auto">
                        Selecciona el modo de visualización para tu estación de trabajo.
                    </p>
                </div>

                <div 
                    className={`grid grid-cols-1 gap-6 md:gap-8 w-full ${
                        (!storageService.getAuthData()?.role || 
                        ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAdminContext()?.role as string) || 
                        ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAuthData()?.role as string) ||
                        ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAuthData()?.staffRole as string))
                            ? 'md:grid-cols-3 max-w-6xl' 
                            : 'md:grid-cols-2 max-w-4xl'
                    }`}
                >
                    {/* Option 1: Public Board */}
                    <button
                        onClick={() => navigate(`/${hospital.handle}/board`)}
                        className="role-card group relative bg-white/80 backdrop-blur-xl p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] shadow-[0_15px_35px_rgba(0,0,0,0.03)] border border-slate-100 text-left flex flex-col items-start overflow-hidden animate-fade-in"
                        style={{ animationDelay: '0.1s' }}
                    >
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                            <Monitor className="w-32 h-32" style={{ color: primaryColor }} />
                        </div>

                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-6 md:mb-8 relative group-hover:scale-110 transition-transform duration-500" style={{ backgroundColor: `${primaryColor}15` }}>
                            <Monitor className="w-7 h-7 md:w-8 md:h-8" style={{ color: primaryColor }} />
                        </div>

                        <h2 className="text-xl md:text-2xl font-black text-slate-800 mb-2 md:mb-3 tracking-tight font-heading">Recepción Digital</h2>
                        <p className="text-slate-500 font-medium leading-relaxed mb-6 md:mb-8 flex-1 text-sm md:text-base">
                            Vista pública en TV para la sala de espera. Muestra el estatus de los pacientes de forma anónima (estilo aeropuerto).
                        </p>

                        <div className="flex items-center gap-2 font-bold text-xs md:text-sm group-hover:gap-4 transition-all uppercase tracking-widest" style={{ color: primaryColor }}>
                            <span>Abrir Monitor</span>
                            <ArrowRight className="w-4 h-4" />
                        </div>
                    </button>

                    {/* Option 2: Admin Dashboard */}
                    <button
                        onClick={() => navigate('/staff/dashboard')}
                        className="role-card-solid group relative p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] text-left flex flex-col items-start overflow-hidden animate-fade-in border border-white/10"
                        style={{
                            backgroundColor: primaryColor,
                            boxShadow: `0 15px 35px -5px ${primaryColor}40`,
                            animationDelay: '0.2s'
                        }}
                    >
                        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-700">
                            <LayoutDashboard className="w-32 h-32 text-white" />
                        </div>

                        <div className="bg-white/20 backdrop-blur-sm w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-6 md:mb-8 relative group-hover:scale-110 transition-transform duration-500 shadow-inner border border-white/10">
                            <LayoutDashboard className="w-7 h-7 md:w-8 md:h-8 text-white drop-shadow-sm" />
                        </div>

                        <h2 className="text-xl md:text-2xl font-black text-white mb-2 md:mb-3 tracking-tight font-heading">Panel Médico</h2>
                        <p className="text-white/70 font-medium leading-relaxed mb-6 md:mb-8 flex-1 text-sm md:text-base">
                            Administración de quirófanos y pacientes. Herramienta de trabajo del médico para cambios clínicos y progreso.
                        </p>

                        <div className="flex items-center gap-2 font-bold text-white text-xs md:text-sm group-hover:gap-4 transition-all uppercase tracking-widest drop-shadow-sm">
                            <span>Entrar al Panel</span>
                            <ArrowRight className="w-4 h-4" />
                        </div>
                    </button>

                    {/* Option 3: Analytics Dashboard */}
                    {(!storageService.getAuthData()?.role || 
                      ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAdminContext()?.role as string) || 
                      ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAuthData()?.role as string) ||
                      ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(storageService.getAuthData()?.staffRole as string)) && (
                    <button
                        onClick={() => navigate((hospital?.id ? `/staff/paneladmin/${hospital.id}` : '/staff/paneladmin') + window.location.search)}
                        className="role-card group relative bg-white/80 backdrop-blur-xl p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] shadow-[0_15px_35px_rgba(0,0,0,0.03)] border border-slate-100 text-left flex flex-col items-start overflow-hidden animate-fade-in"
                        style={{ animationDelay: '0.3s' }}
                    >
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                            <BarChart3 className="w-32 h-32" style={{ color: primaryColor }} />
                        </div>

                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-6 md:mb-8 relative group-hover:scale-110 transition-transform duration-500" style={{ backgroundColor: `${primaryColor}15` }}>
                            <BarChart3 className="w-7 h-7 md:w-8 md:h-8" style={{ color: primaryColor }} />
                        </div>

                        <h2 className="text-xl md:text-2xl font-black text-slate-800 mb-2 md:mb-3 tracking-tight font-heading">Panel Administrativo</h2>
                        <p className="text-slate-500 font-medium leading-relaxed mb-6 md:mb-8 flex-1 text-sm md:text-base">
                            Centro de administración del hospital. Analíticas, publicidad, monitores y gestión de accesos del personal.
                        </p>

                        <div className="flex items-center gap-2 font-bold text-xs md:text-sm group-hover:gap-4 transition-all uppercase tracking-widest" style={{ color: primaryColor }}>
                            <span>Abrir Panel</span>
                            <ArrowRight className="w-4 h-4" />
                        </div>
                    </button>
                    )}
                </div>

                <div className="mt-12 md:mt-16 flex items-center gap-3 md:gap-6 text-slate-300 font-bold text-[8px] md:text-[10px] uppercase tracking-[0.2em] font-heading opacity-70">
                    <span className="flex items-center gap-2"><Building className="w-4 h-4" /> Estación Verificada</span>
                    <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                    <span>Hospital Status Health Systems v2</span>
                </div>
            </main>
        </div>
    );
};

export default HospitalRoleSelection;
